import * as React from 'react';
import { createContext, useContext } from 'react';

import { useExperiment } from '@coursera-web/statsig';

const EXPERIMENT_NAME = 'AI_Powered_Search';
const PARAM_NAME = 'allocation';

export type AiPoweredSearchVariant = 'control' | 'aiOverviewOnly' | 'combined';

export type AiPoweredSearchExperimentValue = {
  assignedVariant: AiPoweredSearchVariant;
  showAiOverview: boolean;
  enableConversationalSearch: boolean;
  enableCompare: boolean;
};

const FEATURES_BY_VARIANT: Record<AiPoweredSearchVariant, AiPoweredSearchExperimentValue> = {
  control: {
    assignedVariant: 'control',
    showAiOverview: false,
    enableConversationalSearch: false,
    enableCompare: false,
  },
  aiOverviewOnly: {
    assignedVariant: 'aiOverviewOnly',
    showAiOverview: true,
    enableConversationalSearch: false,
    enableCompare: false,
  },
  combined: {
    assignedVariant: 'combined',
    showAiOverview: true,
    enableConversationalSearch: true,
    enableCompare: true,
  },
};

const getAssignedVariant = (allocation: unknown): AiPoweredSearchVariant => {
  if (allocation === 'a') return 'aiOverviewOnly';
  if (allocation === 'b') return 'combined';
  return 'control';
};

const AiPoweredSearchExperimentContext = createContext<AiPoweredSearchExperimentValue>(FEATURES_BY_VARIANT.control);

export const useAiPoweredSearchExperiment = () => useContext(AiPoweredSearchExperimentContext);

function AiPoweredSearchExperimentProvider({ children }: React.PropsWithChildren) {
  const experiment = useExperiment(EXPERIMENT_NAME);
  const allocation = experiment.get(PARAM_NAME, 'control');
  const assignedVariant = getAssignedVariant(allocation);

  return (
    <AiPoweredSearchExperimentContext.Provider value={FEATURES_BY_VARIANT[assignedVariant]}>
      {children}
    </AiPoweredSearchExperimentContext.Provider>
  );
}

export default AiPoweredSearchExperimentProvider;
